import { useMemo, useEffect } from "react";
import { useHistory } from "react-router";

import { useEventImages } from "./hooks/useEventImages";
import { useOrderAddress } from "./hooks/useOrderAddress";
import { useOrderData } from "./hooks/useOrderData";
import { useOrderFulfillment } from "./hooks/useOrderFulfillment";
import { useOrderList } from "./hooks/useOrderList";
import { useOrderTransaction } from "./hooks/useOrderTransaction";
import { useReceiptState } from "./hooks/useReceiptState";
import { useTicketBonusMap } from "./hooks/useTicketBonusMap";

import type { PATH_PARAMETERS } from "constants/pathParameters";
import type { GetParameters } from "types/GetParameters";

type Parameters = GetParameters<
  [(typeof PATH_PARAMETERS)["/account/settlement/history/:orderId"]]
>;

type Input = {
  parameters: Parameters;
};

export const useThis = (input: Input) => {
  const {
    parameters: { orderId },
  } = input;
  const history = useHistory();
  const { transaction, isInvalidTransaction } = useOrderTransaction({
    orderId,
  });

  const { addressData, isLoading: isLoadingAddress } = useOrderAddress({
    transaction,
  });
  const { fulfillmentData, isLoading: isLoadingFulfillment } =
    useOrderFulfillment({
      transaction,
    });
  const {
    eventImageMap,
    eventPartsMap,
    isLoading: isImageLoading,
  } = useEventImages({
    transaction,
  });
  const { bonusMap, isLoadingBonus } = useTicketBonusMap({ transaction });

  const { orderData } = useOrderData({ transaction });
  const { orderList } = useOrderList({
    transaction,
    eventImageMap,
    eventPartsMap,
    bonusMap,
    isImageLoading,
  });
  const {
    isDisplayReceipt,
    receiptData,
    handleShowReceipt,
    handleCloseReceipt,
  } = useReceiptState({
    orderData,
    orderList,
  });

  useEffect(() => {
    if (isInvalidTransaction) history.replace("/account/settlement/history");
  }, [isInvalidTransaction, history]);

  const deliveryData = useMemo(() => {
    if (!addressData) return null;

    return {
      recipientName: addressData.recipientName,
      postalCode: addressData.zipCode,
      mainAddress: `${addressData.prefecture}${addressData.city}${addressData.streetNum}`,
      subAddress: addressData.buildingInfo,
    };
  }, [addressData]);

  const handleCopyOrderId = async (orderId: number) => {
    await navigator.clipboard.writeText(String(orderId));
  };

  return {
    deliveryData,
    fulfillmentData,
    handleCloseReceipt,
    handleCopyOrderId,
    handleShowReceipt,
    isDisplayReceipt,
    isLoadingAddress,
    isLoadingBonus,
    isLoadingFulfillment,
    orderData,
    orderList,
    receiptData,
  };
};
